import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';
import { captureException, fetchAllData } from 'r/utils';
import { GET_ISSUERS } from 'r/utils/fetchApiRaw';
import type {
  Analyse,
  Kol,
  KOLsObject,
  IssuerWithPerformanceMetrics,
} from 'r/types';

export const issuersKolsAtom = atomWithQuery(() => ({
  queryKey: ['issuers-kols'],
  queryFn: async () => {
    try {
      const { data: kols }: { data: Array<IssuerWithPerformanceMetrics> } =
        await fetchAllData(GET_ISSUERS, {
          queryParams: {
            filter: '"adminInfo.isKol":true',
          },
        });
      return kols;
    } catch (e) {
      captureException(e);
      throw e;
    }
  },
  gcTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
}));

export const allIssuersAtom = atomWithQuery(() => ({
  queryKey: ['all-issuers'],
  queryFn: async () => {
    try {
      const { data }: { data: Array<IssuerWithPerformanceMetrics> } =
        await fetchAllData(GET_ISSUERS, { queryParams: {} });
      return data;
    } catch (e) {
      captureException(e);
      return [];
    }
  },
  gcTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
}));

export const findTweetByIdAtom = atom(
  null,
  (
    get,
    set,
    { tweetId, analyses }: { tweetId: string; analyses: Analyse[] },
  ): Analyse | undefined => {
    return analyses.find((a) => a.id === tweetId || a.tweetID === tweetId);
  },
);

const mapIssuerToKol = (
  issuer: IssuerWithPerformanceMetrics,
  timeframe: string,
  leadersInfo: KOLsObject = {},
): Kol => {
  const suffixMap: Record<string, '1d' | '1w' | '2w' | '1M'> = {
    '1d': '1d',
    '7d': '1w',
    '14d': '2w',
    '30d': '1M',
  };
  const suffix = suffixMap[timeframe] || '1w';

  const rank = issuer[`rank_${suffix}`] || 0;
  const winRateStr = issuer[`winRate_${suffix}`] || '0';
  const winRate = parseFloat(winRateStr);

  const completedCount = issuer[`completedCount_${suffix}`] || 0;
  const gainCount = issuer[`gainCount_${suffix}`] || 0;
  const winRateText = `${gainCount}/${completedCount}`;

  const sumPerformanceStr = issuer[`sumPerformance_${suffix}`] || '0';
  const performance = parseFloat(sumPerformanceStr);

  const topTokensObj = issuer.topTokens || {};
  const topTokens = Object.entries(topTokensObj)
    .sort(([, a], [, b]) => b - a)
    .map(([token]) => token);

  const key = issuer.username || issuer.twitter || '';
  const leaderInfo = leadersInfo[key];

  const bestPerfVal = parseFloat(issuer[`bestPerformance_${suffix}`] || '0');
  const worstPerfVal = parseFloat(issuer[`worstPerformance_${suffix}`] || '0');

  const bestTweetId = issuer[`bestPerformanceTweetId_${suffix}`];
  const worstTweetId = issuer[`worstPerformanceTweetId_${suffix}`];

  const createStubTweet = (
    perfVal: number,
    id: string | null | undefined,
    isBest: boolean,
  ): Analyse => {
    if (id && typeof id === 'string' && id.trim() !== '') {
      return {
        id,
        tweetID: id,
        performance: perfVal,
        fetchedPerformance: perfVal,
        pair: '',
        tweetText: `${isBest ? 'Best' : 'Worst'} performance tweet`,
        authorName: issuer.username,
        authorUsername: issuer.username,
        isPredictionUp: perfVal > 0,
        openPrice: '0',
        tweetDate: '',
        tweet: {},
        type: perfVal > 0 ? 'buy' : 'sell',
      } as unknown as Analyse;
    }

    return {
      id: `stub-${isBest ? 'best' : 'worst'}-${issuer.id}`,
      tweetID: '',
      performance: perfVal.toString(),
      fetchedPerformance: perfVal,
      pair: '',
      tweetText: '',
      authorName: issuer.username,
      authorUsername: issuer.username,
      isPredictionUp: perfVal > 0,
      openPrice: '0',
      tweetDate: '',
      tweet: {},
      type: perfVal > 0 ? 'buy' : 'sell',
    } as unknown as Analyse;
  };

  return {
    rank,
    totalRank: 0,
    name: `@${issuer.username}`,
    avatarUrl: issuer.avatar || '',
    defaultTweet: [],
    authorUserName: issuer.username,
    analysisAuthorId: issuer.id,
    winRateText,
    winRate,
    predictions: issuer[`predictionsCount`] || 0,
    bestPerformanceTweet: createStubTweet(bestPerfVal, bestTweetId, true),
    worstPerformanceTweet: createStubTweet(worstPerfVal, worstTweetId, false),
    deletedTweets: issuer.deletedTweetsCount || 0,
    avgPerformance: parseFloat(issuer[`avgPerformance_${suffix}`] || '0'),
    performance,
    topTokens,
    quickInsight: leaderInfo?.text || issuer.adminInfo?.insight || null,
    gainVsLoss: {
      gain: gainCount,
      loss: issuer[`lossCount_${suffix}`] || 0,
    },
    shortVsLong: {
      short: issuer.shortCount || 0,
      long: issuer.longCount || 0,
    },
  };
};

export const featuredKolAtomFamily = atomFamily((timeframe: string) =>
  atom((get) => {
    const issuersKols = get(issuersKolsAtom);
    if (!issuersKols.data) {
      return [];
    }
    const kols = issuersKols.data.map((issuer) =>
      mapIssuerToKol(issuer, timeframe),
    );
    return kols.sort((a, b) => a.rank - b.rank);
  }),
);
